import React, { useRef, useState, useCallback } from "react";
import { CircularProgress, Tooltip } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import uploadIcon from "../../assets/images/upload_icon_blue.svg";
import excelIcon from "../../assets/images/excel.png";
import styles from "./PayrollUploadModal.module.scss";
import * as XLSX from "xlsx";
import moment from "moment";
const REQUIRED_HEADERS = [
  "Client ID",
  "Location Name",
  "Location Fed ID#",
  "Client Start Date",
  "Employee Name",
  "SS#",
  "Employee Hire Date",
  "WKC Code",
  "Last Check Date",
  "Hourly Wage",
  "DOB",
  "Total Pay",
  "Pay Excluded/OT Pay",
];

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function formatDate(date) {
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function generateUUID() {
  return (
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15)
  );
}

// Added 'error' to props
function FileItem({ file, isLoading, onRemove }) {
  return (
    <div className={`${styles.fileRow} ${file.error ? styles.errorRow : ""}`}>
      <div className={styles.fileIconWrapper}>
        <img src={excelIcon} alt="Excel" width={32} height={32} />
      </div>

      <div className={styles.fileMeta}>
        <div className={styles.fileName}>{file.file.name}</div>
        <div className={styles.fileSub}>
          <span>{formatBytes(file.file.size)}</span>
          <span className={styles.dotSeparator}>•</span>
          <span>{formatDate(new Date(file.file.lastModified))}</span>
        </div>
      </div>

      <div className={styles.actionArea}>
        {/* Info Icon on Error */}
        {file.error && (
          <Tooltip
            title={file.error}
            arrow
            placement="left"
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: "white",
                  color: "#ef4444",
                  fontSize: "12px",
                  padding: "8px 12px",
                  border: "1px solid #ffa39e",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  "& .MuiTooltip-arrow": {
                    color: "white",
                    "&::before": {
                      border: "1px solid #ffa39e",
                    },
                  },
                },
              },
            }}
          >
            <InfoOutlinedIcon className={styles.infoIconError} />
          </Tooltip>
        )}

        {isLoading ? (
          <CircularProgress
            size={20}
            thickness={4}
            className={styles.loaderIcon}
          />
        ) : (
          <button
            onClick={() => onRemove(file.uuid)}
            className={styles.removeBtn}
            title="Remove file"
          >
            <ClearIcon className={styles.clearIcon} />
          </button>
        )}
      </div>

      {/* Static Error Message */}
      {file.error && <div className={styles.errorStaticMsg}>Validation failed</div>}
    </div>
  );
}

export default function PayrollUploadModal({ open, onClose, onSubmit }) {
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const listRef = useRef(null);

  const ACCEPTED = [".xls", ".xlsx"];

  const addFiles = useCallback((incoming) => {
    const valid = Array.from(incoming).filter((f) =>
      ACCEPTED.some((ext) => f.name.toLowerCase().endsWith(ext)),
    );
    setFiles((prev) => {
      const availableSpace = 10 - prev.length;
      if (availableSpace <= 0) {
        alert("You can only upload a maximum of 10 files.");
        return prev;
      }
      
      const filesToAdd = valid.slice(0, availableSpace);
      if (valid.length > availableSpace) {
        alert(`You can only upload a maximum of 10 files. Only ${availableSpace} file(s) were added.`);
      }

      const enriched = filesToAdd.map((f) => ({
        uuid: generateUUID(),
        file: f,
        error: null,
      }));
      return [...prev, ...enriched];
    });
  }, []);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const handleRemove = (uuidToRemove) => {
    setFiles((prev) => prev.filter((f) => f.uuid !== uuidToRemove));
  };

const handleSubmit = async () => {
  if (!files.length || isSubmitting) return;

  setIsSubmitting(true);
  let globalHasError = false;
  const validatedFiles = [...files];
  const finalDataForParent = [];

  for (let i = 0; i < validatedFiles.length; i++) {
    try {
      const fileObj = validatedFiles[i];
      const data = await fileObj.file.arrayBuffer();

      const workbook = XLSX.read(data, { 
        type: "array", 
        cellDates: true, 
        sheetRows: 2 
      });
      
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];

      const rowsAsArrays = XLSX.utils.sheet_to_json(sheet, { header: 1 });
      const rawHeaders = rowsAsArrays[0] || [];
      const normalizedHeaders = rawHeaders.map((h) => String(h).trim());
      const dataRows = XLSX.utils.sheet_to_json(sheet, { raw: false });

      const missing = REQUIRED_HEADERS.filter(
        (h) => !normalizedHeaders.includes(h)
      );

      console.log('Normalized headers:', normalizedHeaders);
      console.log('Missing headers:', missing);

      let errorMsg = null;

      if (missing.length > 0) {
        errorMsg = `Missing headers: ${missing.join(", ")}`;
      } else if (dataRows.length === 0) {
        errorMsg = "File is empty or contains no data rows";
      } else {
        const firstRow = dataRows[0];
        const clientIdKey = Object.keys(firstRow).find(k => k.trim() === "Client ID");
        const dateKey = Object.keys(firstRow).find(k => k.trim() === "Last Check Date");

        const clientIdValue = firstRow[clientIdKey];
        const dateValue = firstRow[dateKey];

        console.log('Client ID value:', clientIdValue);
        console.log('Date value:', dateValue);

        if (!clientIdValue) {
          errorMsg = "Missing Client ID value in first row";
        } else if (!dateValue) {
          errorMsg = "Missing Last Check Date value";
        } else {
          const d = moment(dateValue, ["MM/DD/YYYY", "MM/DD/YY", "M/D/YYYY", "M/D/YY"]);
          
          finalDataForParent.push({
            file: fileObj.file,
            clientId: String(clientIdValue).trim(),
            lastCheckDate: d.isValid() ? d.format("M/D/YYYY") : "N/A",
          });
        }
      }

      // 4. Update file error status
      if (errorMsg) {
        validatedFiles[i].error = errorMsg;
        globalHasError = true;
      } else {
        validatedFiles[i].error = null;
      }
    } catch (err) {
      console.error("Error parsing file:", err);
      validatedFiles[i].error = "Invalid Excel format or corrupted file";
      globalHasError = true;
    }
  }

  // MUST update state so UI shows red rows
  setFiles(validatedFiles);

  if (globalHasError) {
    setIsSubmitting(false);
    // Auto-scroll to error
    setTimeout(() => {
      const firstErrorIndex = validatedFiles.findIndex((f) => f.error);
      if (firstErrorIndex !== -1 && listRef.current) {
        const errorElement = listRef.current.children[firstErrorIndex];
        errorElement?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }, 100);
    return; // STOP HERE
  }

  // 5. Proceed to parent upload ONLY if no errors
  const loadingMap = {};
  files.forEach((f) => (loadingMap[f.uuid] = true));
  setLoadingFiles(loadingMap);

  try {
    if (onSubmit) await onSubmit(finalDataForParent);
    setFiles([]);
    if (onClose) onClose();
  } catch (err) {
    console.error("Upload error:", err);
  } finally {
    setLoadingFiles({});
    setIsSubmitting(false);
  }
};

  const handleClose = () => {
    if (isSubmitting) return;
    setFiles([]);
    setLoadingFiles({});
    if (onClose) onClose();
  };

  if (!open) return null;

  return (
    <div
      className={styles.overlay}
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div className={styles.modal}>
        <div className={styles.headerRow}>
          <div className={styles.titleWrapper}>
            <h2 className={styles.title}>Please Upload Payroll File</h2>
          </div>
          <button
            className={styles.closeBtn}
            onClick={handleClose}
            disabled={isSubmitting}
            aria-label="Close modal"
          >
            <ClearIcon className={styles.closeIcon} />
          </button>
        </div>

        <div
          className={`${styles.dropZone} ${isDragging ? styles.isDragging : ""}`}
          onClick={() => !isSubmitting && fileInputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <div className={styles.dropHeader}>
            <img src={uploadIcon} alt="Upload" className={styles.dropIcon} />
            <p className={styles.dropTitle}>Drag &amp; drop files or Browse</p>
          </div>
          <p className={styles.dropSub}>
            Supported formats: Excel (.xls , .xlsx), Max file size: 10MB,
            <br />
            Upload up to 10 Payroll files at once.
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xls,.xlsx"
            multiple
            style={{ display: "none" }}
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = "";
            }}
          />
        </div>

        {files.length > 0 ? (
          <div ref={listRef} className={styles.fileListContainer}>
            {files.map((file) => (
              <FileItem
                key={file.uuid}
                file={file}
                isLoading={!!loadingFiles[file.uuid]}
                onRemove={handleRemove}
              />
            ))}
          </div>
        ) : (
          <div className={styles.emptyList}>No files selected yet</div>
        )}

        <button
          className={styles.submitBtn}
          onClick={handleSubmit}
          disabled={!files.length || isSubmitting}
        >
          {isSubmitting && !Object.values(loadingFiles).some((v) => v) ? (
            <>
              <CircularProgress size={20} thickness={5} color="inherit" />
              <span>Validating...</span>
            </>
          ) : isSubmitting ? (
            <>
              <CircularProgress size={20} thickness={5} color="inherit" />
              <span>Uploading...</span>
            </>
          ) : (
            "Upload Files"
          )}
        </button>
      </div>
    </div>
  );
}
